import { useNavigate, useParams } from 'react-router-dom';
import Topbar from '../components/Topbar';
import DarkCard from '../components/DarkCard';
import DarkButton from '../components/DarkButton';
import ThemeRoot from '../components/ThemeRoot';
import StageHomework from '../components/StageHomework';
import { useDarkStore } from '../store/darkStore';
import type { StageQuestion } from '../../data/stageConfigs';
import { haptic } from '../lib/haptic';

/** Format a saved answer back to something human-readable per question type. */
function formatAnswer(question: StageQuestion, value: string): string {
  const v = (value ?? '').toString().trim();
  if (!v) return '';
  switch (question.type) {
    case 'emojiSlider': {
      const idx = parseInt(v, 10);
      const stop = question.emojiStops?.[idx];
      return stop ? `${stop.emoji} ${stop.label}` : v;
    }
    case 'slider': {
      const labels: string[] = [];
      if (question.sliderMinLabel) labels.push(question.sliderMinLabel);
      if (question.sliderMaxLabel) labels.push(question.sliderMaxLabel);
      return labels.length ? `${v} · (${labels.join(' ↔ ')})` : v;
    }
    case 'freeText':
    case 'textArea':
    case 'multipleChoice':
    default:
      return v;
  }
}

export default function StageReview() {
  const navigate = useNavigate();
  const { stageId } = useParams<{ stageId: string }>();
  const id = parseInt(stageId ?? '1', 10);

  const { stages, answers } = useDarkStore();
  const stage = stages.find((s) => s.id === id);

  if (!stage) {
    return (
      <ThemeRoot>
        <Topbar />
        <div className="mx-auto max-w-xl px-4 py-10 text-center">
          <h1 className="font-marker text-3xl text-amber">Stage not found</h1>
          <DarkButton
            accent="amber"
            className="mt-6"
            onClick={() => navigate('/home')}
          >
            Back to Launchpad
          </DarkButton>
        </div>
      </ThemeRoot>
    );
  }

  const stageAnswers = answers[stage.key] ?? {};
  const filledCount = stage.questions.filter(
    (q) => (stageAnswers[q.id] ?? '').trim().length > 0,
  ).length;

  return (
    <ThemeRoot>
      <Topbar />

      <main className="mx-auto max-w-2xl px-4 py-6 md:py-10">
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-2 font-marker text-sm uppercase tracking-[0.2em] text-amber/90">
            <span className="text-base">{stage.emoji}</span>
            <span>
              Stage {stage.id} · {stage.name}
            </span>
          </div>
          <span className="rounded-full border border-mint/40 bg-mint/10 px-3 py-1 font-marker text-xs text-mint">
            Completed · {filledCount} / {stage.questions.length}
          </span>
        </div>

        <DarkCard>
          <h2 className="font-marker text-2xl text-ink md:text-3xl">
            Your answers
          </h2>
          <p className="mt-1 font-gill text-sm text-ink/70">
            Read-only review. Jump back to redo the stage if you want to
            sharpen anything.
          </p>

          <ul className="mt-5 space-y-3">
            {stage.questions.map((q, i) => {
              const rawValue = stageAnswers[q.id] ?? '';
              const formatted = formatAnswer(q, rawValue);
              return (
                <li
                  key={q.id}
                  className="rounded-2xl border border-white/10 bg-base/60 p-4"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-amber/40 bg-amber/10 font-marker text-xs text-amber">
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-marker text-[11px] uppercase tracking-[0.18em] text-ink/60">
                        {q.prompt}
                      </div>
                      <div className="mt-1 font-gill text-base text-ink">
                        {formatted || (
                          <span className="italic text-ink/50">
                            No answer saved
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </DarkCard>

        {/* Homework preview */}
        <DarkCard className="mt-5">
          <div className="mb-3 flex items-center gap-2">
            <span className="rounded-full border border-violet/40 bg-violet/10 px-3 py-1 font-marker text-sm text-violet-glow">
              📓 Homework
            </span>
            <span className="font-marker text-[11px] uppercase tracking-wider text-ink/50">
              Your saved work
            </span>
          </div>
          <StageHomework stageKey={stage.key} />
        </DarkCard>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <DarkButton
            accent="ghost"
            onClick={() => {
              haptic('tap');
              navigate('/home');
            }}
          >
            Back to Launchpad
          </DarkButton>
          <DarkButton
            accent="amber"
            onClick={() => {
              haptic('tap');
              navigate(`/home/challenge/${stage.id}`);
            }}
          >
            Redo this stage
          </DarkButton>
        </div>
      </main>
    </ThemeRoot>
  );
}
